/**
 * Professional Draft Invoice Page
 * Public page for viewing draft invoices via token link
 * Designed to look like a real, printable invoice
 */

import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { format } from 'date-fns';
import { Printer, Download, AlertCircle, Truck, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

// Fetch draft invoice by token (basic endpoint)
// Use relative path in dev mode for Vite proxy
const isDevelopment = import.meta.env.DEV;
const COMMERCE_SERVICE_BASE_URL = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');

interface LineItem {
  lineItemId?: number;
  lineNumber?: number;
  description: string;
  hsnCode?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  totalTax?: number;
  totalAmount?: number;
}

interface InvoiceData {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  status: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerGstin?: string;
  customerAddress?: string;
  customerState?: string;
  vendorName?: string;
  vendorEmail?: string;
  vendorPhone?: string;
  vendorGstin?: string;
  vendorAddress?: string;
  vendorState?: string;
  vendorCity?: string;
  vendorPostalCode?: string;
  subtotal: number;
  gstAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceDue: number;
  orderNumber?: string;
  orderId?: number;
  dealCodes?: string;
  lineItems: LineItem[];
  note?: string;
}

interface DraftInvoiceResponse {
  status: 'success' | 'error';
  invoice?: InvoiceData;
  message?: string;
  isDraft: boolean;
}

async function fetchDraftInvoice(token: string): Promise<DraftInvoiceResponse> {
  const response = await fetch(
    `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/draft/${token}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch draft invoice: ${response.status}`);
  }

  return response.json();
}

/**
 * Currency formatter for INR
 */
const formatCurrency = (amount: number | null | undefined): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount ?? 0);
};

/**
 * Number to words conversion for Indian currency
 */
const numberToWords = (num: number): string => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanThousand = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };

  if (num === 0) return 'Zero Rupees Only';

  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = Math.floor(num % 1000);
  const paise = Math.round((num % 1) * 100);

  let result = '';
  if (crore) result += convertLessThanThousand(crore) + ' Crore ';
  if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
  if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
  if (remainder) result += convertLessThanThousand(remainder);

  result = result.trim() + ' Rupees';
  if (paise > 0) {
    result += ' and ' + convertLessThanThousand(paise) + ' Paise';
  }
  result += ' Only';

  return result;
};

/**
 * Loading Skeleton Component
 */
const LoadingSkeleton = () => (
  <div className="min-h-screen bg-gray-100 py-8">
    <div className="max-w-4xl mx-auto bg-white shadow-lg">
      <Skeleton className="h-32 w-full" />
      <div className="p-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  </div>
);

/**
 * Error Component
 */
const ErrorDisplay = ({ message }: { message: string }) => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    <div className="max-w-md w-full">
      <Alert variant="destructive" className="bg-white">
        <AlertCircle className="h-5 w-5" />
        <AlertTitle className="text-lg font-semibold">Unable to Load Invoice</AlertTitle>
        <AlertDescription className="mt-2">
          <p>{message}</p>
          <p className="mt-4 text-sm text-gray-600">
            This link may have expired or is no longer valid. Please contact the sender for a new link.
          </p>
        </AlertDescription>
      </Alert>
    </div>
  </div>
);

/**
 * Main Professional Invoice Component
 */
export const DraftInvoicePage = () => {
  const { token } = useParams<{ token: string }>();
  const invoiceRef = useRef<HTMLDivElement>(null);

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['draft-invoice', token],
    queryFn: () => fetchDraftInvoice(token!),
    enabled: !!token,
    retry: 1,
    staleTime: 1000 * 60 * 5,
  });

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError || !response || response.status === 'error') {
    return (
      <ErrorDisplay
        message={
          (error as Error)?.message ||
          response?.message ||
          'Invalid or expired invoice access link'
        }
      />
    );
  }

  const invoice = response.invoice;

  if (!invoice) {
    return <ErrorDisplay message="Invoice data not found" />;
  }

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .invoice-container, .invoice-container * {
            visibility: visible;
          }
          .invoice-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 py-4 sm:py-8 px-2 sm:px-4">
        {/* Action Buttons - Hide on print */}
        <div className="no-print max-w-4xl mx-auto mb-4 flex justify-end gap-2">
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="h-4 w-4" />
            Print Invoice
          </Button>
          <Button variant="default" onClick={handlePrint} className="gap-2">
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        </div>

        {/* Invoice Container */}
        <div
          ref={invoiceRef}
          className="invoice-container max-w-4xl mx-auto bg-white shadow-xl print:shadow-none"
        >
          {/* Header Section */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              {/* Company Info */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">SAUBHAGYA</h1>
                <div className="mt-3 text-emerald-100 text-xs sm:text-sm space-y-1">
                  <p className="flex items-center gap-2">
                    <MapPin className="h-3 w-3" />
                    Hyderabad, Telangana, India
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="h-3 w-3" />
                    +91 40 1234 5678
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    info@saubhagya.com
                  </p>
                </div>
              </div>

              {/* Invoice Title */}
              <div className="text-right">
                <div className="inline-block">
                  <h2 className="text-3xl sm:text-4xl font-bold">INVOICE</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Details Bar */}
          <div className="bg-gray-50 border-b px-6 sm:px-8 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Invoice No.</p>
                <p className="font-bold text-gray-900">{invoice.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Invoice Date</p>
                <p className="font-semibold text-gray-900">
                  {invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'dd MMM yyyy') : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Due Date</p>
                <p className="font-semibold text-gray-900">
                  {invoice.dueDate ? format(new Date(invoice.dueDate), 'dd MMM yyyy') : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Order Ref.</p>
                <p className="font-semibold text-gray-900">{invoice.orderNumber || '-'}</p>
              </div>
            </div>
          </div>

          {/* Bill To Section */}
          <div className="px-6 sm:px-8 py-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Billed By (Vendor) */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">
                  Billed By (Vendor)
                </h3>
                <div className="space-y-1 text-sm">
                  <p className="font-bold text-lg text-gray-900">
                    {invoice.vendorName || 'SAUBHAGYA'}
                  </p>
                  {invoice.vendorAddress && (
                    <p className="text-gray-600">{invoice.vendorAddress}</p>
                  )}
                  {(invoice.vendorCity || invoice.vendorState) && (
                    <p className="text-gray-600">
                      {invoice.vendorCity}
                      {invoice.vendorCity && invoice.vendorState ? ', ' : ''}
                      {invoice.vendorState}
                    </p>
                  )}
                  {invoice.vendorPostalCode && (
                    <p className="text-gray-600">{invoice.vendorCity} - {invoice.vendorPostalCode}</p>
                  )}
                  {invoice.vendorPhone && (
                    <p className="text-gray-600 flex items-center gap-1 mt-2">
                      <Phone className="h-3 w-3" /> {invoice.vendorPhone}
                    </p>
                  )}
                  {invoice.vendorEmail && (
                    <p className="text-gray-600 flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {invoice.vendorEmail}
                    </p>
                  )}
                  {invoice.vendorGstin && (
                    <p className="text-gray-600 mt-2">
                      <span className="font-medium">GSTIN:</span> {invoice.vendorGstin}
                    </p>
                  )}
                </div>
              </div>

              {/* Bill To (Customer) */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Bill To (Customer)
                </h3>
                <div className="space-y-1">
                  <p className="font-bold text-lg text-gray-900">{invoice.customerName}</p>
                  {invoice.customerAddress && (
                    <p className="text-gray-600 text-sm">{invoice.customerAddress}</p>
                  )}
                  {invoice.customerState && (
                    <p className="text-gray-600 text-sm">{invoice.customerState}</p>
                  )}
                  {invoice.customerPhone && (
                    <p className="text-gray-600 text-sm flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {invoice.customerPhone}
                    </p>
                  )}
                  {invoice.customerEmail && (
                    <p className="text-gray-600 text-sm flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {invoice.customerEmail}
                    </p>
                  )}
                  {invoice.customerGstin && (
                    <p className="text-gray-600 text-sm mt-2">
                      <span className="font-medium">GSTIN:</span> {invoice.customerGstin}
                    </p>
                  )}
                </div>
              </div>

              {/* Delivery Info */}
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Delivery Information
                </h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p>Your order is being processed and will be delivered soon.</p>
                  {invoice.dealCodes && (
                    <p className="mt-2">
                      <span className="font-medium">Deal Code:</span> {invoice.dealCodes}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="px-6 sm:px-8 pb-6">
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="text-left py-3 px-4 font-semibold">#</th>
                    <th className="text-left py-3 px-4 font-semibold">Description</th>
                    <th className="text-left py-3 px-4 font-semibold hidden sm:table-cell">HSN</th>
                    <th className="text-right py-3 px-4 font-semibold">Qty</th>
                    <th className="text-right py-3 px-4 font-semibold">Rate</th>
                    <th className="text-right py-3 px-4 font-semibold hidden sm:table-cell">Tax</th>
                    <th className="text-right py-3 px-4 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoice.lineItems && invoice.lineItems.length > 0 ? (
                    invoice.lineItems.map((item, index) => {
                      const taxAmount = item.totalTax ?? ((item.cgstAmount ?? 0) + (item.sgstAmount ?? 0) + (item.igstAmount ?? 0));
                      const lineTotal = item.totalAmount ?? ((item.subtotal ?? (item.quantity * item.unitPrice)) + taxAmount);
                      const taxRate = (item.cgstRate ?? 0) + (item.sgstRate ?? 0) + (item.igstRate ?? 0);

                      return (
                        <tr key={item.lineItemId || index} className="hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-500">{index + 1}</td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{item.description}</p>
                              {item.unit && (
                                <p className="text-xs text-gray-500">Unit: {item.unit}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600 hidden sm:table-cell">
                            {item.hsnCode || '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-medium">
                            {item.quantity}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className="py-3 px-4 text-right hidden sm:table-cell">
                            <div>
                              <p>{formatCurrency(taxAmount)}</p>
                              {taxRate > 0 && (
                                <p className="text-xs text-gray-500">@{taxRate}%</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-gray-900">
                            {formatCurrency(lineTotal)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">
                        No items in this invoice
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary Section */}
          <div className="px-6 sm:px-8 pb-6">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Amount in Words */}
              <div className="flex-1">
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Amount in Words
                  </p>
                  <p className="text-gray-800 font-medium italic">
                    {numberToWords(invoice.totalAmount)}
                  </p>
                </div>

                {/* Bank Details */}
                <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                    Bank Details for Payment
                  </p>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p><span className="font-medium">Bank:</span> State Bank of India</p>
                    <p><span className="font-medium">Account Name:</span> SAUBHAGYA Biogas Pvt Ltd</p>
                    <p><span className="font-medium">Account No:</span> XXXX XXXX 1234</p>
                    <p><span className="font-medium">IFSC:</span> SBIN0001234</p>
                    <p><span className="font-medium">Branch:</span> Hyderabad Main Branch</p>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="w-full sm:w-80">
                <div className="bg-gray-50 rounded-lg border overflow-hidden">
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
                    </div>

                    {/* GST Breakdown */}
                    {(invoice.cgstAmount > 0 || invoice.sgstAmount > 0) && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">CGST</span>
                          <span>{formatCurrency(invoice.cgstAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">SGST</span>
                          <span>{formatCurrency(invoice.sgstAmount)}</span>
                        </div>
                      </>
                    )}

                    {invoice.igstAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">IGST</span>
                        <span>{formatCurrency(invoice.igstAmount)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="text-gray-600">Total Tax (GST)</span>
                      <span className="font-medium">{formatCurrency(invoice.gstAmount)}</span>
                    </div>
                  </div>

                  {/* Grand Total */}
                  <div className="bg-emerald-600 text-white p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">GRAND TOTAL</span>
                      <span className="text-2xl font-bold">{formatCurrency(invoice.totalAmount)}</span>
                    </div>
                  </div>

                  {/* Payment Status */}
                  {invoice.paidAmount > 0 && (
                    <div className="p-4 bg-white border-t space-y-2">
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Amount Paid</span>
                        <span className="font-medium">{formatCurrency(invoice.paidAmount)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold">
                        <span className={invoice.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}>
                          Balance Due
                        </span>
                        <span className={invoice.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatCurrency(invoice.balanceDue)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Terms & Notes */}
          <div className="px-6 sm:px-8 pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Terms */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Terms & Conditions
                </h3>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Payment is due within 30 days of invoice date.</li>
                  <li>• Late payments may attract interest @ 18% per annum.</li>
                  <li>• Goods once sold will not be taken back.</li>
                  <li>• Subject to Hyderabad jurisdiction only.</li>
                </ul>
              </div>

              {/* Note */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Notes
                </h3>
                <p className="text-xs text-gray-600">
                  {invoice.note || 'Thank you for your business! We appreciate your trust in SAUBHAGYA.'}
                </p>
              </div>
            </div>
          </div>

          {/* Signature Section */}
          <div className="px-6 sm:px-8 pb-8">
            <div className="flex justify-end">
              <div className="text-center">
                <div className="h-16 w-48 border-b-2 border-gray-300 mb-2"></div>
                <p className="text-sm font-medium text-gray-700">Authorized Signatory</p>
                <p className="text-xs text-gray-500">SAUBHAGYA Biogas Pvt Ltd</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-800 text-white px-6 sm:px-8 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs">
              <p>This is a computer-generated invoice and does not require a physical signature.</p>
              <p>&copy; {new Date().getFullYear()} SAUBHAGYA</p>
            </div>
          </div>

        </div>

        {/* Help Text - Hide on print */}
        <div className="no-print max-w-4xl mx-auto mt-6 text-center text-sm text-gray-500">
          <p>
            For any queries regarding this invoice, please contact our support team at{' '}
            <a href="mailto:support@saubhagya.com" className="text-emerald-600 hover:underline">
              support@saubhagya.com
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default DraftInvoicePage;
